import {
  useFetchUrl,
  useAuthoringEnvironmentInfo,
} from '@web-config-app/core-react';
import type { DataSourceConsumer } from '@web-config-app/core';
import { getMappedDataSource } from '../../utils/get-mapped-data-source/get-mapped-data-source';
import type { UrlDataSource } from '../../types/data-source';
import { useEntityDetailsProps } from '../use-entity-details-props/use-entity-details-props';

/**
 * Props for the useUrlDataSource hook
 */
export interface UseUrlDataSourceProps {
  /** URL data source containing the endpoint to fetch data from */
  dataSource: UrlDataSource;
  /** Configuration for how the fetched data should be consumed and mapped */
  dataSourceConsumer: DataSourceConsumer;
}

/**
 * Hook that fetches and processes data from a URL data source
 *
 * This hook handles fetching data from a URL endpoint and applying mapping transformations
 * if needed. It uses the current authoring environment to determine the API URL.
 *
 * @param {UseUrlDataSourceProps} props - The URL data source props
 * @param {UrlDataSource} props.dataSource - The URL data source containing the endpoint
 * @param {DataSourceConsumer} props.dataSourceConsumer - Configuration for how the data should be consumed
 * @returns {object} Object containing the fetched data (possibly transformed) and query properties
 */

export const useUrlDataSource = ({
  dataSource,
  dataSourceConsumer,
}: UseUrlDataSourceProps) => {
  const { currentAuthoringEnvironment } = useEntityDetailsProps();
  const { apiUrl, audience } = useAuthoringEnvironmentInfo(
    currentAuthoringEnvironment,
  );
  const { data, ...queryProps } = useFetchUrl({
    // This is kinda weird, ...
    path: dataSource.value,
    apiUrl,
    audience,
  });

  const finalData =
    dataSourceConsumer.mapping && Array.isArray(data)
      ? getMappedDataSource({
          dataSourceValue: data,
          type: dataSourceConsumer.type,
          mapping: dataSourceConsumer.mapping,
        })
      : data;

  return {
    ...queryProps,
    data: finalData,
  };
};
